import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MessageSquare, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Navigation, Footer } from '../landing/LandingPage';
import { apiClient } from '../../api/client';
import { Dropdown, DropdownTrigger, DropdownMenu, DropdownItem } from '../../components/inputs/Dropdown';
import { Checkbox } from '../../components/inputs/Checkbox';

const SUBJECTS = [
  'General question',
  'Sales / pricing',
  'Technical support',
  'Feature request',
  'Data access or deletion request',
  'Press / media',
  'Other',
];

const INPUT_CLS = 'w-full px-4 py-3 rounded-xl border border-border bg-background-card text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/40 text-sm transition-colors';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

export function PublicContactPage() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '', _trap: '' });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!privacyAccepted) {
      setStatus('error');
      setErrorMsg('Please accept the privacy policy to continue.');
      return;
    }
    if (form._trap) return; // honeypot
    if (!form.subject) {
      setStatus('error');
      setErrorMsg('Please select a subject.');
      return;
    }
    setStatus('sending');
    setErrorMsg('');
    try {
      const { _trap, ...payload } = form;
      await apiClient.post('/api/v1/contact', { ...payload, source: 'website' });
      setStatus('success');
      setForm({ name: '', email: '', subject: '', message: '', _trap: '' });
      setPrivacyAccepted(false);
    } catch (err: any) {
      setStatus('error');
      setErrorMsg(err?.response?.data?.message || 'Something went wrong. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navigation />

      <main className="flex-1">
        {/* Header */}
        <section className="pt-32 pb-16 md:pt-40 md:pb-20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div initial={false} animate="visible" variants={stagger}>
              <motion.div variants={fadeInUp} className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 mb-6">
                <MessageSquare className="w-7 h-7 text-primary" />
              </motion.div>
              <motion.h1 variants={fadeInUp} className="text-3xl md:text-4xl font-semibold text-text-primary mb-4">
                Get in touch
              </motion.h1>
              <motion.p variants={fadeInUp} className="text-lg text-text-secondary max-w-xl mx-auto">
                Have a question or need help? We'd love to hear from you.
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* Form + Info */}
        <section className="pb-24">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-3 gap-12">

              {/* Left: contact info */}
              <div className="space-y-8">
                <div>
                  <h3 className="text-sm font-semibold text-text-primary uppercase tracking-wider mb-3">Contact</h3>
                  <p className="text-text-secondary text-sm">Use the form to get in touch. We aim to respond within one business day, but usually much quicker.</p>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-text-primary uppercase tracking-wider mb-3">Already have an account?</h3>
                  <Link to="/login" className="text-sm font-medium text-primary hover:underline">
                    Sign in to contact support →
                  </Link>
                </div>
              </div>

              {/* Right: form */}
              <div className="md:col-span-2">
                {status === 'success' ? (
                  <motion.div
                    initial={false}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex flex-col items-center justify-center text-center py-16 gap-4"
                  >
                    <div className="flex items-center justify-center w-14 h-14 rounded-full bg-success/10">
                      <CheckCircle className="w-7 h-7 text-success" />
                    </div>
                    <h2 className="text-xl font-semibold text-text-primary">Message sent!</h2>
                    <p className="text-text-secondary">Thanks for reaching out. We'll get back to you within one business day.</p>
                    <button onClick={() => setStatus('idle')} className="mt-2 text-sm text-primary hover:underline">
                      Send another message
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-1.5">Name</label>
                        <input type="text" name="name" value={form.name} onChange={handleChange} required placeholder="Your name" className={INPUT_CLS} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-1.5">Email</label>
                        <input type="email" name="email" value={form.email} onChange={handleChange} required placeholder="you@example.com" className={INPUT_CLS} />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1.5">Subject</label>
                      <Dropdown
                        value={form.subject}
                        onSelect={(val) => setForm(prev => ({ ...prev, subject: val }))}
                      >
                        <DropdownTrigger placeholder="Select a subject…">
                          {form.subject || undefined}
                        </DropdownTrigger>
                        <DropdownMenu>
                          {SUBJECTS.map(s => (
                            <DropdownItem key={s} value={s}>{s}</DropdownItem>
                          ))}
                        </DropdownMenu>
                      </Dropdown>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1.5">Message</label>
                      <textarea name="message" value={form.message} onChange={handleChange} required rows={6} placeholder="Tell us what's on your mind…" className={`${INPUT_CLS} resize-none`} />
                    </div>

                    {/* Honeypot */}
                    <input type="text" name="_trap" value={form._trap} onChange={handleChange} tabIndex={-1} aria-hidden="true" className="sr-only" autoComplete="off" />

                    <Checkbox
                      checked={privacyAccepted}
                      onChange={setPrivacyAccepted}
                      label={
                        <span className="text-sm text-text-secondary font-normal">
                          I agree to the{' '}
                          <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
                          {' '}and consent to being contacted about my enquiry.
                        </span>
                      }
                    />

                    {status === 'error' && (
                      <p className="flex items-center gap-2 text-sm text-error">
                        <AlertCircle className="w-4 h-4 shrink-0" />
                        {errorMsg}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={status === 'sending' || !privacyAccepted}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-white font-medium text-sm hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <Send className="w-4 h-4" />
                      {status === 'sending' ? 'Sending…' : 'Send message'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
